// Contents
import ProjectElement from "./ProjectElement.jsx";
import ProjectFilters from "./ProjectFilters.jsx";
import React, { useState, useMemo, useEffect } from "react";

export default function ProjectList({ navigate, lang, data, uiMap })
{
    const DATA = Array.isArray(data) ? data : [];

    const ALL_VALUE = "";

    const agencies = useMemo(() => {
        const map = new Map();
        DATA.forEach(d => {
            const a = (d?.agency ?? '').trim();
            if (a) {
                const key = a.toLowerCase();
                if (!map.has(key)) map.set(key, a);
            }
        });
        const arr = Array.from(map.values());
        arr.sort((a, b) => a.localeCompare(b, lang === 'fr' ? 'fr' : 'en', { sensitivity: 'base' }));
        return [ALL_VALUE, ...arr];
    }, [DATA, lang]);

    const [selectedAgency, setSelectedAgency] = useState(ALL_VALUE);

    const PAGE_SIZE = 6;
    const [displayCount, setDisplayCount] = useState(PAGE_SIZE);

    useEffect(() => {
        setDisplayCount(PAGE_SIZE);
    }, [selectedAgency, DATA]);

    useEffect(() => {
        setSelectedAgency(ALL_VALUE);
    }, [lang]);

    const visibleData = useMemo(() => {
        const matchesAgency = (d) => {
            if (selectedAgency === ALL_VALUE) return true;
            return ((d?.agency ?? '').trim().toLowerCase()) === selectedAgency.toLowerCase();
        };

        const listWithIndex = DATA.map((d, idx) => ({ d, originalIndex: idx })).filter(({ d }) => matchesAgency(d));

        const extractYear = (val) => {
            if (val == null) return 0;
            const m = String(val).match(/(\d{4})/);
            return m ? parseInt(m[1], 10) : 0;
        };

        listWithIndex.sort((a, b) => {
            const ya = extractYear(a.d?.date);
            const yb = extractYear(b.d?.date);
            if (ya !== yb) return yb - ya;
            return a.originalIndex - b.originalIndex;
        });

        return listWithIndex.map(x => x.d);
    }, [DATA, selectedAgency, lang]);

    const pagedData = visibleData.slice(0, displayCount);

    const handleLoadMore = () => {
        setDisplayCount(c => c + PAGE_SIZE);
    };

    const loadMoreLabel = uiMap?.['load-more']?.[lang] || (lang === 'fr' ? 'Voir plus' : 'See more');

    return (
        <div className="projects-list">
            <ProjectFilters agencies={agencies} selected={selectedAgency} onChange={setSelectedAgency} lang={lang} uiMap={uiMap} />

            <ul className="projects-list-inner">
                {pagedData.map((d, i) => {
                    return (
                        <ProjectElement key={d?.slug ?? i} navigate={navigate} data={d} lang={lang} />
                    );
                })}
            </ul>

            {visibleData.length > pagedData.length && (
                <div className="projects-load-more">
                    <button onClick={handleLoadMore} aria-label="See more projects" className="button btn-load-more">
                        <span>{loadMoreLabel}</span>
                        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
                            <polygon points="11.293 4.707 17.586 11 4 11 4 13 17.586 13 11.293 19.293 12.707 20.707 21.414 12 12.707 3.293 11.293 4.707"/>
                        </svg>
                    </button>
                </div>
            )}
        </div>
    )
}
